import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Control, FieldValues, useForm } from 'react-hook-form';
import BigNumber from 'bignumber.js';
import { makeStyles } from 'tss-react/mui';

import { mapDataToUndefinedIfSkip, useLazyQueryDebounce } from 'modules/api';
import { useConnection } from 'modules/auth';
import {
  getFirstError,
  MIN_STAKE_VALUE,
  NumberTextField,
  ZERO,
} from 'modules/common';
import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';
import {
  useGetBalancesQuery,
  useLazyGetStakeGasEstimationQuery,
  useStakeMutation,
} from 'modules/stake';

import { useGetStatsQuery } from '../../actions/getStats';
import { ActionButton } from './ActionButton';
import { Details } from './Details';
import { translation } from './translation';

const mergedTranslation = mergeTranslations(globalTranslation, translation);

interface IFormValues extends FieldValues {
  amount: string;
}

interface IStakeFormProps {
  stakeAmount?: BigNumber;
}

export function StakeForm({ stakeAmount }: IStakeFormProps): ReactElement {
  const { classes, cx } = useStyles();
  const { t, keys } = useTranslation(mergedTranslation);
  const [isMax, setIsMax] = useState(false);

  const [stake, { isLoading }] = useStakeMutation();

  const { isConnected, connectionKey } = useConnection();

  const { data: getBalanceData } = useGetBalancesQuery(
    { connectionKey },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
    },
  );

  const balanceNative = getBalanceData?.native ?? ZERO;
  const balanceStaked = getBalanceData?.staked ?? ZERO;

  const { data: stats } = useGetStatsQuery();

  const {
    control,
    formState: { errors, isValid, isValidating },
    handleSubmit,
    watch,
    setValue,
    reset,
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: {
      amount: stakeAmount?.toString() ?? '',
    },
  });

  const isBoostRewards = !!stakeAmount;

  const amountInputValue = watch('amount');

  const convertedAmount = useMemo(() => {
    const convertedValue = new BigNumber(amountInputValue);

    return convertedValue.isNaN() ? ZERO : convertedValue;
  }, [amountInputValue]);

  const iotaUsdPrice = stats?.iotaUsdPrice ?? ZERO;
  const amountUsd = convertedAmount.multipliedBy(iotaUsdPrice);

  const isSubmitDisabled = !isValid || isLoading;

  const onSubmit = async ({ amount }: { amount: BigNumber }) => {
    const { error } = await stake({ amount });
    if (!error) {
      reset();
    }
  };

  const [
    getStakeGasEstimation,
    {
      isLoading: isGasEstimationLoading,
      currentData: gasEstimationCurrentData,
    },
  ] = useLazyQueryDebounce(useLazyGetStakeGasEstimationQuery);

  useEffect(() => {
    if (
      isValid &&
      !isValidating &&
      amountInputValue &&
      new BigNumber(amountInputValue).isGreaterThanOrEqualTo(MIN_STAKE_VALUE) &&
      !isMax
    ) {
      void getStakeGasEstimation({ amount: amountInputValue }, true);
    }
  }, [amountInputValue, getStakeGasEstimation, isMax, isValid, isValidating]);

  const handleChange = useCallback(() => {
    setIsMax(false);
  }, []);

  const onMaxClick = useCallback(async () => {
    if (!getBalanceData?.native) {
      return;
    }

    setIsMax(true);

    if (getBalanceData.native.isLessThan(MIN_STAKE_VALUE)) {
      setValue('amount', getBalanceData.native.toString(), {
        shouldValidate: true,
      });
      return;
    }

    const { data: gasEstimationCurrentData } = await getStakeGasEstimation(
      {
        amount: getBalanceData.native,
      },
      true,
      true,
    );

    if (!gasEstimationCurrentData) {
      setIsMax(false);
      return;
    }

    const amount = getBalanceData.native.minus(
      gasEstimationCurrentData.estimatedGas,
    );

    if (amount.isLessThanOrEqualTo(0)) {
      setValue('amount', '0', { shouldValidate: true });
      return;
    }

    setValue('amount', amount.toString(), { shouldValidate: true });
  }, [getBalanceData, getStakeGasEstimation, setValue]);

  return (
    <form
      onChange={handleChange}
      onSubmit={handleSubmit(({ amount }) =>
        onSubmit({ amount: new BigNumber(amount) }),
      )}
    >
      <div
        className={cx(
          classes.balanceWrap,
          isBoostRewards && classes.boostRewards,
        )}
      >
        <div className={classes.amount}>{t(keys.amount)}</div>

        {!stakeAmount && (
          <div>
            {t(keys.balance, {
              value: balanceNative?.decimalPlaces(4).toFormat(),
              token: t(keys.tokens.native),
            })}
          </div>
        )}
      </div>

      <NumberTextField
        isStakeTab
        amountUsd={amountUsd}
        balance={balanceNative}
        control={control as unknown as Control<FieldValues>}
        disabled={isLoading || isBoostRewards}
        error={getFirstError(errors)}
        isBoostRewards={isBoostRewards}
        maxButtonProps={{
          disabled: !getBalanceData?.native || isGasEstimationLoading || isMax,
        }}
        min={MIN_STAKE_VALUE}
        name="amount"
        placeholder="0"
        unit={t(keys.tokens.native)}
        variant="outlined"
        onMaxClick={onMaxClick}
      />

      <div
        className={cx(classes.action, isBoostRewards && classes.boostAction)}
      >
        <ActionButton
          isStake
          isLoading={isLoading}
          isSubmitDisabled={isSubmitDisabled}
          isZeroAmount={convertedAmount.isZero()}
        />
      </div>

      <Details
        isStakeTab
        amount={convertedAmount}
        fee={gasEstimationCurrentData?.estimatedGas}
        staked={balanceStaked}
      />
    </form>
  );
}

const useStyles = makeStyles()(theme => ({
  balanceWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 14,
    marginTop: theme.spacing(7),

    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(6),
    },
  },
  boostRewards: {
    marginTop: 0,
  },
  amount: {
    fontWeight: 600,
  },

  action: {
    marginTop: theme.spacing(9),
  },

  loader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
  },

  boostAction: {
    marginTop: theme.spacing(4),
  },
}));
